import {useCallback} from 'react';
import {UploadResult} from '../types';
import joinPath from '../../lib/joinPath.ts';
import useClientConfig from '../../hooks/useClientConfig.tsx';
import {useAuthStateManager} from '../../hooks/useAuthStateManager.tsx';
import {filesIn, uploadFileInChunks} from './chunkedUpload.ts';

/*
 * Uploads the files in a FormData (any number under the name "file"), one
 * after another, each in chunks (chunkedUpload.ts). The FormData is only the
 * shape the callers have always built; nothing is posted as a form any more.
 * The results are what the old upload-files route answered with, so the
 * callers read them unchanged. UploadFromComputer uses the chunked uploader
 * directly, for progress.
 */
const useUploadFiles = (
    containerName?: string | null,
    folderPath?: string[] | string | null
) => {
    const joinedPath = Array.isArray(folderPath)
        ? joinPath(folderPath)
        : folderPath;
    const {apiServiceBaseUri} = useClientConfig();
    const {getAccessToken} = useAuthStateManager();

    return useCallback(
        async (data: FormData): Promise<UploadResult[]> => {
            const results: UploadResult[] = [];

            for (const file of filesIn(data)) {
                results.push(
                    ...(await uploadFileInChunks({
                        file,
                        containerName,
                        folderPath: joinedPath,
                        apiServiceBaseUri,
                        getAccessToken
                    }))
                );
            }

            return results;
        },
        [containerName, joinedPath, apiServiceBaseUri, getAccessToken]
    );
};

export default useUploadFiles;
